import { Badge } from "@volca/ui/components/ui/badge";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import type { StatusColor } from "@volca/ui/tokens/component-colors";
import { MARK_LABEL, type CellMark } from "./comparison-grid";

/** A formatted cell reads as money; this reads it back so the gap can be shown. */
export const amount = (value: string | undefined) => {
  if (!value) return null;
  // Strip only the marks a formatter adds, then insist on one whole number.
  // Stripping everything non-numeric would read "$300 / $200" as 300200 and a
  // date as a quantity, and then confidently report a change that never
  // happened. A cell it cannot read is a cell with no difference to state.
  const cleaned = value.replace(/[$,\s]/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  return Number(cleaned);
};

export const difference = (
  before: string | undefined,
  after: string | undefined,
) => {
  const from = amount(before);
  const to = amount(after);
  if (from === null || to === null || from === to) return null;
  const gap = to - from;
  const money = /[$]/.test(before ?? "") || /[$]/.test(after ?? "");
  const size = Math.abs(gap).toLocaleString(undefined, {
    minimumFractionDigits: money ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `${gap > 0 ? "Up" : "Down"} ${money ? "$" : ""}${size}`;
};

const STATUS = {
  changed: "warning",
  format: "neutral",
  added: "success",
  removed: "danger",
} as const satisfies Record<CellMark, StatusColor>;

const MONO = "font-mono text-xs";

/**
 * What one cell says on both sides.
 *
 * A coordinate is the wrong unit for someone reading their own payroll: nobody
 * knows what S107 is. The row and column labels the sheet already carries turn
 * it back into "Andrew S · Total", which is the question they actually have,
 * and the before and after read as one struck-through line and one new one.
 */
export const CellInspector = ({
  coord,
  address,
  labels,
  before,
  after,
  mark,
}: {
  coord: string | null;
  address: string | null;
  labels: { row_label?: string; column_label?: string } | undefined;
  before: string | undefined;
  after: string | undefined;
  mark: CellMark | undefined;
}) => {
  if (!coord)
    return (
      <p className="flex min-h-[38px] shrink-0 items-center border-t border-line px-4 text-xs text-subtle italic">
        Select a cell to see what changed.
      </p>
    );

  const named = [labels?.row_label, labels?.column_label]
    .filter(Boolean)
    .join(" · ");
  const gap = difference(before, after);

  return (
    <Stack
      gap={0}
      align="stretch"
      direction="row"
      className="flex max-h-32 min-h-[38px] shrink-0 flex-wrap items-center gap-x-4 gap-y-2 overflow-auto border-t border-line px-4 py-2 text-xs"
    >
      <span className="flex items-center gap-2">
        <span className="font-mono font-medium">{address}</span>
        <Badge
          size="sm"
          variant="secondary"
          color={mark ? STATUS[mark] : "neutral"}
          className="font-semibold"
        >
          {mark ? MARK_LABEL[mark] : "Unchanged"}
        </Badge>
      </span>
      {!!named && <span className="text-subtle">{named}</span>}
      <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-line bg-neutral px-2 py-0.5">
        <span className="font-semibold text-subtle">Value</span>
        {/* An unchanged cell has one value, not a before and an after: struck
            through and arrowed, the same number twice reads as a change. */}
        {mark ? (
          <>
            <span
              className={cn(
                MONO,
                "max-w-[260px] truncate text-danger line-through",
              )}
              title={before || "empty"}
            >
              {before || "empty"}
            </span>
            <span className="text-subtle">→</span>
            <span
              className={cn(MONO, "max-w-[260px] truncate text-success")}
              title={after || "empty"}
            >
              {after || "empty"}
            </span>
          </>
        ) : (
          <span
            className={cn(MONO, "max-w-[420px] truncate")}
            title={after || before || "empty"}
          >
            {after || before || "empty"}
          </span>
        )}
      </span>
      {!!gap && <span className="font-semibold">{gap}</span>}
    </Stack>
  );
};
